import countryCodesData from '../data/country-codes.json';

const PHONE_LENGTH_RULES_BY_COUNTRY = {
  IN: [10],
  US: [10],
  CA: [10],
  GB: [10],
  AU: [9],
  AE: [9],
  SA: [9],
  SG: [8],
  MY: [9, 10],
  TH: [9],
  PH: [10],
  ID: [9, 10, 11, 12],
  VN: [9, 10],
  JP: [10],
  CN: [11],
  KR: [9, 10],
  HK: [8],
  TW: [9],
  NZ: [8, 9],
  DE: [10, 11],
  FR: [9],
  IT: [9, 10],
  ES: [9],
  NL: [9],
  BE: [9],
  CH: [9],
  AT: [10, 11],
  SE: [9],
  NO: [8],
  DK: [8],
  FI: [9, 10],
  PL: [9],
  PT: [9],
  GR: [10],
  IE: [9],
  BR: [10, 11],
  MX: [10],
  AR: [10],
  CL: [9],
  CO: [10],
  PE: [9],
  ZA: [9],
  EG: [10],
  KE: [9],
  NG: [10],
  GH: [9],
  PK: [10],
  BD: [10],
  LK: [9],
  NP: [10],
  BT: [8],
  MV: [7],
  AF: [9],
  IR: [10],
  IQ: [10],
  IL: [9],
  TR: [10],
  RU: [10],
  UA: [9],
  KZ: [10],
  UZ: [9],
};

const DEFAULT_LENGTH_RANGE = { min: 6, max: 15 };

const SORTED_DIAL_CODES = [...countryCodesData].sort(
  (left, right) => right.dialCode.length - left.dialCode.length
);

export function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '');
}

export function getCountryCodeMeta(dialCode) {
  const normalized = String(dialCode || '').trim();
  return (
    countryCodesData.find((country) => country.dialCode === normalized) ||
    countryCodesData.find((country) => country.code === 'IN') ||
    null
  );
}

export function getPhoneLengthRule(dialCode) {
  const country = getCountryCodeMeta(dialCode);
  const lengths = country ? PHONE_LENGTH_RULES_BY_COUNTRY[country.code] : null;
  return {
    country,
    lengths: Array.isArray(lengths) && lengths.length ? lengths : null,
    min: DEFAULT_LENGTH_RANGE.min,
    max: DEFAULT_LENGTH_RANGE.max,
  };
}

export function getPhoneMaxLength(dialCode) {
  const { lengths, max } = getPhoneLengthRule(dialCode);
  return lengths ? Math.max(...lengths) : max;
}

export function formatPhoneLengthLabel(dialCode) {
  const { country, lengths, min, max } = getPhoneLengthRule(dialCode);
  const countryLabel = country?.name ? ` for ${country.name}` : '';
  if (lengths) {
    const label = lengths.length === 1 ? `${lengths[0]}` : `${Math.min(...lengths)}-${Math.max(...lengths)}`;
    return `${label} digit${lengths.length === 1 && lengths[0] === 1 ? '' : 's'}${countryLabel}`;
  }
  return `${min}-${max} digits${countryLabel}`;
}

export function validatePhoneForCountry(phone, dialCode, { required = true, label = 'Mobile number' } = {}) {
  const digits = digitsOnly(phone);
  if (!digits) {
    return required ? `${label} is required` : '';
  }

  const { lengths, min, max } = getPhoneLengthRule(dialCode);
  if (lengths) {
    return lengths.includes(digits.length)
      ? ''
      : `${label} must be ${formatPhoneLengthLabel(dialCode)}`;
  }

  return digits.length >= min && digits.length <= max
    ? ''
    : `${label} must be ${formatPhoneLengthLabel(dialCode)}`;
}

export function isValidPhoneForCountry(phone, dialCode, options) {
  return !validatePhoneForCountry(phone, dialCode, options);
}

export function normalizePhoneInputForCountry(value, dialCode) {
  let digits = digitsOnly(value);
  if (String(dialCode || '').trim() === '+91') {
    digits = digits.replace(/^0+/, '');
  }
  return digits.slice(0, getPhoneMaxLength(dialCode));
}

export function splitPhoneWithDialCode(rawPhone, fallbackDialCode = '+91') {
  const phoneValue = String(rawPhone || '').trim();
  if (!phoneValue) {
    return { dialCode: fallbackDialCode, nationalNumber: '' };
  }

  const compactWithPlus = phoneValue.startsWith('+')
    ? `+${phoneValue.slice(1).replace(/\D/g, '')}`
    : '';
  if (compactWithPlus) {
    const matchedDialCode = SORTED_DIAL_CODES.find((country) =>
      compactWithPlus.startsWith(country.dialCode)
    );
    if (matchedDialCode) {
      return {
        dialCode: matchedDialCode.dialCode,
        nationalNumber: normalizePhoneInputForCountry(
          compactWithPlus.slice(matchedDialCode.dialCode.length),
          matchedDialCode.dialCode
        ),
      };
    }
  }

  const phoneDigits = digitsOnly(phoneValue);
  if (phoneDigits) {
    const matchedFromDigits = SORTED_DIAL_CODES.find((country) => {
      const dialCodeDigits = digitsOnly(country.dialCode);
      if (!phoneDigits.startsWith(dialCodeDigits)) return false;
      const national = phoneDigits.slice(dialCodeDigits.length);
      if (!national.length) return false;
      const { lengths, min, max } = getPhoneLengthRule(country.dialCode);
      const nationalLength = national.length;
      if (lengths) {
        return lengths.includes(nationalLength);
      }
      return nationalLength >= min && nationalLength <= max;
    });
    if (matchedFromDigits) {
      const dialCodeDigits = digitsOnly(matchedFromDigits.dialCode);
      return {
        dialCode: matchedFromDigits.dialCode,
        nationalNumber: normalizePhoneInputForCountry(
          phoneDigits.slice(dialCodeDigits.length),
          matchedFromDigits.dialCode
        ),
      };
    }
  }

  return {
    dialCode: fallbackDialCode,
    nationalNumber: normalizePhoneInputForCountry(phoneDigits, fallbackDialCode),
  };
}

export function formatPhoneWithDialCode(dialCode, nationalPhone) {
  const nationalDigits = digitsOnly(nationalPhone);
  if (!nationalDigits) return '';
  const normalizedDialCode = String(dialCode || '+91').replace(/\s/g, '') || '+91';
  return `${normalizedDialCode}${nationalDigits}`;
}

/** Map ISO code, country name, or dial code string to a dial code (e.g. IN → +91). */
export function resolveDialCodeForCountryInput(
  countryInput,
  countryCodes = countryCodesData,
  countries = null
) {
  const raw = String(countryInput || '').trim();
  if (!raw) return null;

  const upper = raw.toUpperCase();
  let match = countryCodes.find((cc) => cc.code === upper);
  if (match) return match.dialCode;

  match = countryCodes.find((cc) => cc.name.toLowerCase() === raw.toLowerCase());
  if (match) return match.dialCode;

  if (Array.isArray(countries)) {
    const countryObj = countries.find(
      (c) =>
        c?.code === upper ||
        c?.name === raw ||
        (c?.name && c.name.toLowerCase() === raw.toLowerCase())
    );
    if (countryObj?.code) {
      match = countryCodes.find((cc) => cc.code === countryObj.code);
      if (match) return match.dialCode;
    }
  }

  if (raw.startsWith('+')) {
    match = countryCodes.find((cc) => cc.dialCode === raw);
    if (match) return match.dialCode;
  }

  return null;
}

export function resolveCountryNameForInput(countryInput, countries = null) {
  const raw = String(countryInput || '').trim();
  if (!raw || !Array.isArray(countries)) return raw;
  const upper = raw.toUpperCase();
  const byCode = countries.find((c) => c?.code === upper);
  if (byCode?.name) return byCode.name;
  const byName = countries.find(
    (c) => c?.name === raw || (c?.name && c.name.toLowerCase() === raw.toLowerCase())
  );
  return byName?.name || raw;
}

/** Map country name or ISO code to ISO 3166-1 alpha-2 for API payloads. */
export function resolveCountryIsoForInput(countryInput, countries = null) {
  const raw = String(countryInput || '').trim();
  if (!raw) return '';
  const upper = raw.toUpperCase();
  if (/^[A-Z]{2}$/.test(upper)) return upper;

  if (Array.isArray(countries)) {
    const byName = countries.find(
      (c) =>
        c?.name === raw ||
        c?.code === upper ||
        (c?.name && c.name.toLowerCase() === raw.toLowerCase())
    );
    if (byName?.code) return byName.code;
  }

  return '';
}
